/*
 * Copyright (c) 2026 Paxia AI Pty Ltd. All rights reserved.
 * This software is proprietary and confidential.
 * No part of this codebase may be copied, modified, published, distributed, sublicensed, or used without prior written permission from Paxia AI Pty Ltd.
 */

import { useState } from "react";
import { completeOnboarding } from "../api/api";

const VncModal = ({ vncDetails, internalAccountId, closeModal, refreshAccounts }) => {
  const [loading, setLoading] = useState(true);

  // Build noVNC URL through the proxy on the SAME origin as the frontend.
  // In production, nginx's ^~ /novnc/ location proxies to the MT5 VM.
  // In dev, the Vite novnc-proxy plugin does the same.
  // path=websockify is relative to the iframe's page URL (/novnc/{port}/vnc.html),
  // so the browser resolves it to /novnc/{port}/websockify — which is correct
  // for both nginx and the Vite proxy.
  const port = vncDetails.novnc_port;
  const hostSegment = vncDetails.novnc_host ? `${encodeURIComponent(vncDetails.novnc_host)}/` : "";
  const origin = window.location.origin;
  const vncUrl = `${origin}/novnc/${hostSegment}${port}/vnc.html?autoconnect=true&resize=scale&password=${encodeURIComponent(vncDetails.vnc_password || '')}&path=websockify`;

  const handleLoginSuccess = async () => {
    try {
      await completeOnboarding(internalAccountId);
      refreshAccounts();
      closeModal();
    } catch (err) {
      console.error("Error completing onboarding:", err);
      alert("Failed to complete onboarding.");
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50">
      <div className="bg-backgroundSurface p-6 rounded-lg shadow-lg w-10/12 h-[90%]">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-xl font-semibold text-textPrimary">VNC Session</h3>
          <button
            type="button"
            onClick={closeModal}
            className="text-textSecondary hover:text-destructive text-2xl font-bold"
          >
            ✕
          </button>
        </div>
        <div className="w-full h-[calc(100%-80px)] border border-borderDefault rounded-lg overflow-hidden relative">
          {loading && (
            <div className="absolute inset-0 flex items-center justify-center bg-black/80 z-10">
              <div className="text-center">
                <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-primary mx-auto mb-3"></div>
                <p className="text-textSecondary text-sm">Connecting to VNC session...</p>
                <p className="text-textSecondary text-xs mt-1">MT5 terminal may take a moment to appear</p>
              </div>
            </div>
          )}
          <iframe
            src={vncUrl}
            className="w-full h-full"
            title="VNC Session"
            onLoad={() => setLoading(false)}
          />
        </div>
        <div className="flex justify-end gap-2 mt-4">
          <button type="button" onClick={closeModal} className="btn btn-secondary">Close</button>
          <button type="button" onClick={handleLoginSuccess} className="btn btn-primary">Login Successful</button>
        </div>
      </div>
    </div>
  );
};

export default VncModal;
